import { reportError } from "./reportError";

const INDEXED_DB_DISCONNECT_PATTERNS = [
  /Connection to Indexed Database server lost/i,
  /indexed\s*db.*(connection|server).*(lost|closed|disconnected)/i,
  /indexeddb.*refresh the page to try again/i,
  /an internal error was encountered in the indexed database server/i,
  /indexeddb.*internal error/i,
];

const SAFARI_EMPTY_RANGES_PATTERNS = [
  /Can't find variable:\s*EmptyRanges/i,
  /ReferenceError:\s*Can't find variable:\s*EmptyRanges/i,
];

const CHUNK_LOAD_FAILURE_PATTERNS = [
  /Load failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/i,
];

const CHUNK_MIME_MISMATCH_PATTERNS = [
  /is not a valid JavaScript MIME type/i,
  /Failed to load module script: Expected a JavaScript module script but the server responded with a MIME type/i,
  /Expected a JavaScript module script but the server responded with a MIME type of ["']text\/html["']/i,
];

/**
 * External DOM mutation (Google Translate, translation extensions, in-app
 * browser text processing) rewriting text nodes under React: the next commit
 * throws NotFoundError from removeChild/insertBefore. The last pattern is
 * WebKit's generic NotFoundError phrasing — it deliberately requires the
 * "NotFoundError" name so a bare occurrence of the sentence elsewhere never
 * triggers a reload.
 */
const DOM_MUTATION_PATTERNS = [
  /Failed to execute 'removeChild' on 'Node'/i,
  /Failed to execute 'insertBefore' on 'Node'/i,
  /NotFoundError:?\s.*The object can not be found here/i,
];

export const INDEXEDDB_RELOAD_SESSION_KEY = "kfa_indexeddb_reload_once";
export const CHUNK_RELOAD_SESSION_KEY = "kfa_chunk_reload_once";
export const DOM_MUTATION_RELOAD_SESSION_KEY = "kfa_dom_mutation_reload_once";

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return `${error.name}: ${error.message}`;
  }
  return String(error);
}

export function isIndexedDbDisconnectMessage(message: string): boolean {
  return INDEXED_DB_DISCONNECT_PATTERNS.some((pattern) => pattern.test(message));
}

export function isIndexedDbDisconnectError(error: unknown): boolean {
  return isIndexedDbDisconnectMessage(getErrorMessage(error));
}

export function isSafariEmptyRangesMessage(message: string): boolean {
  return SAFARI_EMPTY_RANGES_PATTERNS.some((pattern) => pattern.test(message));
}

export function isSafariEmptyRangesError(error: unknown): boolean {
  return isSafariEmptyRangesMessage(getErrorMessage(error));
}

export function isChunkLoadFailureMessage(message: string): boolean {
  return (
    CHUNK_LOAD_FAILURE_PATTERNS.some((pattern) => pattern.test(message)) ||
    isChunkMimeTypeMismatchMessage(message)
  );
}

export function isChunkLoadError(error: unknown): boolean {
  return isChunkLoadFailureMessage(getErrorMessage(error));
}

export function isChunkMimeTypeMismatchMessage(message: string): boolean {
  return CHUNK_MIME_MISMATCH_PATTERNS.some((pattern) => pattern.test(message));
}

export function isChunkMimeTypeMismatchError(error: unknown): boolean {
  return isChunkMimeTypeMismatchMessage(getErrorMessage(error));
}

export function isDomMutationMessage(message: string): boolean {
  return DOM_MUTATION_PATTERNS.some((pattern) => pattern.test(message));
}

export function isDomMutationError(error: unknown): boolean {
  return isDomMutationMessage(getErrorMessage(error));
}

export type DomMutationKind =
  | "removechild_notfound"
  | "insertbefore_notfound"
  | "webkit_notfound_generic";

/** Symptom subfamily for Sentry diagnostics; null when the message is not this family. */
export function getDomMutationKind(message: string): DomMutationKind | null {
  if (DOM_MUTATION_PATTERNS[0].test(message)) return "removechild_notfound";
  if (DOM_MUTATION_PATTERNS[1].test(message)) return "insertbefore_notfound";
  if (DOM_MUTATION_PATTERNS[2].test(message)) return "webkit_notfound_generic";
  return null;
}

/**
 * One-shot reload triggered when an IndexedDB disconnect is detected outside a
 * DOM event (e.g., from a Firestore onSnapshot error callback or an
 * ErrorBoundary render). Uses sessionStorage so we reload at most once per
 * session; returns true if a reload was triggered (caller should assume the
 * page is about to unload).
 */
export function attemptIndexedDbRecovery(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(INDEXEDDB_RELOAD_SESSION_KEY) === "1") {
      return false;
    }
    window.sessionStorage.setItem(INDEXEDDB_RELOAD_SESSION_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * One-shot reload triggered when a dynamic-import chunk fetch fails (typically
 * because a new deploy rotated the hashed asset filenames while the old
 * index.html is still in memory). Uses sessionStorage so we reload at most
 * once per session; returns true if a reload was triggered.
 */
export function attemptChunkReloadRecovery(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) === "1") {
      return false;
    }
    window.sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * One-shot reload after an external DOM mutation (page translation etc.)
 * crashed React's commit and reached the route error boundary. The
 * domMutationGuard prevents most of these throws; this is the backstop for
 * phrasings the guard cannot intercept. Uses sessionStorage so we reload at
 * most once per session; returns true if a reload was triggered.
 */
export function attemptDomMutationRecovery(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(DOM_MUTATION_RELOAD_SESSION_KEY) === "1") {
      return false;
    }
    window.sessionStorage.setItem(DOM_MUTATION_RELOAD_SESSION_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Listener errors repeat on every Firestore retry; cap reports per scope per
 * page load so a dead listener cannot flood Sentry/PostHog quota.
 */
const SNAPSHOT_ERROR_REPORT_CAP = 3;
const snapshotErrorReportCounts = new Map<string, number>();

/**
 * Shared error handler for Firestore onSnapshot listeners. Reports every
 * listener error to Sentry and PostHog (rate-limited per scope), triggers the
 * one-shot reload for IndexedDB disconnects, and falls through to the
 * caller's handler.
 */
export function handleSnapshotError(
  scope: string,
  error: Error,
  userOnError?: (error: Error) => void
): void {
  const isIndexedDbDisconnect = isIndexedDbDisconnectError(error);
  const reportCount = snapshotErrorReportCounts.get(scope) ?? 0;
  if (reportCount < SNAPSHOT_ERROR_REPORT_CAP) {
    snapshotErrorReportCounts.set(scope, reportCount + 1);
    reportError(error, {
      area: "firestore",
      op: "onsnapshot",
      tags: {
        error_path: "firestore_onsnapshot",
        firestore_observer_scope: scope,
        ...(isIndexedDbDisconnect
          ? { recovery_error_family: "indexeddb_disconnect" }
          : {}),
      },
    });
  }
  if (isIndexedDbDisconnect && attemptIndexedDbRecovery()) {
    return;
  }
  console.error(`Error observing ${scope}:`, error);
  userOnError?.(error);
}
